import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { ArrowBack, BookmarkBorder, Bookmark } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import Spinner from '../components/Spinner';
import { readSchoolBookmarks, toggleSchoolBookmark } from '../utils/schoolBookmarks';

interface AssignmentItem {
  _id: string;
  title: string;
  description: string;
  subject: { _id: string; name: string };
  className: string;
  instructions: string;
  totalPoints: number;
  dueDate: string;
  attachmentUrl: string;
  attachmentName: string;
  isPublished: boolean;
  score?: number;
  maxScore?: number;
  feedback?: string;
  status?: string;
}

const StudentAssignments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<AssignmentItem[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [bookmarkedIds, setBookmarkedIds] = useState<Record<string, boolean>>({});

  const refreshBookmarks = async () => {
    const saved = await readSchoolBookmarks();
    const next: Record<string, boolean> = {};
    saved.forEach((bookmark) => {
      next[`${bookmark.type}:${bookmark.id}`] = true;
    });
    setBookmarkedIds(next);
  };

  const getAssignmentSubjectGroupKey = (assignment: AssignmentItem) => {
    const name = assignment.subject?.name?.trim();
    return name ? name.toLowerCase() : 'general';
  };

  const getAssignmentSubjectGroupLabel = (assignment: AssignmentItem) => {
    const name = assignment.subject?.name?.trim();
    return name || 'General/Uncategorized';
  };

  const isHighSchoolEducation = (educationLevel?: string) =>
    typeof educationLevel === 'string' && /high[- ]school/i.test(educationLevel);

  const hasSchoolWorkAccess = Boolean(
    isHighSchoolEducation(user?.educationLevel) &&
    (user?.school || user?.schoolName || user?.schoolEmail)
  );

  useEffect(() => {
    const fetchAssignments = async () => {
      setLoading(true);
      setError(null);
      try {
        const [assignmentsRes, gradesRes] = await Promise.all([
          api.get('/school-work/assignments'),
          api.get('/school-work/grades')
        ]);

        const assignmentData: AssignmentItem[] = Array.isArray(assignmentsRes.data) ? assignmentsRes.data : [];
        const gradeItems = Array.isArray(gradesRes.data) ? gradesRes.data : [];
        const gradeIndex = new Map<string, { score?: number; maxScore?: number; feedback?: string; status?: string }>();

        gradeItems.forEach((grade: any) => {
          const assignmentId = grade.assignment?._id || grade.assignment;
          if (assignmentId) {
            gradeIndex.set(assignmentId.toString(), {
              score: typeof grade.score === 'number' ? grade.score : undefined,
              maxScore: typeof grade.maxScore === 'number' ? grade.maxScore : undefined,
              feedback: typeof grade.feedback === 'string' ? grade.feedback : undefined,
              status: typeof grade.status === 'string' ? grade.status : undefined
            });
          }
        });

        setAssignments(
          assignmentData.map((assignment) => ({
            ...assignment,
            ...(gradeIndex.get(assignment._id) || {})
          }))
        );
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load assignments');
      } finally {
        setLoading(false);
      }
    };
    
    if (hasSchoolWorkAccess) {
      fetchAssignments();
      refreshBookmarks().catch(() => setError('Unable to load saved bookmarks'));
    } else {
      setLoading(false);
    }
  }, [hasSchoolWorkAccess, user?._id]);

  const handleToggleBookmark = async (assignment: AssignmentItem) => {
    try {
      const isBookmarked = await toggleSchoolBookmark({
      id: assignment._id,
      type: 'assignment',
      title: assignment.title,
      subject: assignment.subject?.name || 'General',
      route: `/school-work/assignments/${assignment._id}`,
      details: `${assignment.className || 'All classes'} • ${assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}`,
      savedAt: new Date().toISOString(),
      });
      setBookmarkedIds((current) => ({ ...current, [`assignment:${assignment._id}`]: isBookmarked }));
    } catch {
      setError('Unable to update saved assignment. Please sign in and try again.');
    }
  };

  if (!hasSchoolWorkAccess && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          School Work is available only to high school students...
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Assignments</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/school-work')}>
          Back to Dashboard
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Assignments from your teachers</Typography>
          {assignments.length === 0 ? (
            <Alert severity="info">No assignments available yet.</Alert>
          ) : !selectedSubjectId ? (
            <Grid container spacing={3}>
              {Object.values(
                assignments.reduce((acc, assignment) => {
                  const subjectKey = getAssignmentSubjectGroupKey(assignment);
                  const subjectName = getAssignmentSubjectGroupLabel(assignment);
                  if (!acc[subjectKey]) {
                    acc[subjectKey] = { _id: subjectKey, name: subjectName, count: 0 };
                  }
                  acc[subjectKey].count += 1;
                  return acc;
                }, {} as Record<string, { _id: string; name: string; count: number }>)
              ).map((subject: any) => (
                <Grid item xs={12} sm={6} md={4} key={subject._id}>
                  <Card 
                    sx={{ cursor: 'pointer', transition: '0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}
                    onClick={() => {
                      setSelectedSubjectId(subject._id);
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>{subject.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {subject.count} Assignment{subject.count !== 1 ? 's' : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              <Button sx={{ mb: 2 }} onClick={() => {
                setSelectedSubjectId(null);
              }} startIcon={<ArrowBack />}>
                Back to Subjects
              </Button>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Feedback</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments
                      .filter(a => getAssignmentSubjectGroupKey(a) === selectedSubjectId)
                      .map((assignment) => (
                      <TableRow key={assignment._id}>
                        <TableCell>{assignment.title}</TableCell>
                        <TableCell>{assignment.subject?.name || 'General'}</TableCell>
                        <TableCell>{assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No deadline'}</TableCell>
                        <TableCell>
                          {typeof assignment.score === 'number'
                            ? `${assignment.score} / ${assignment.maxScore ?? assignment.totalPoints}`
                            : 'Pending'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {assignment.feedback ? assignment.feedback : 'No feedback yet'}
                        </TableCell>
                        <TableCell>{assignment.totalPoints}</TableCell>
                        <TableCell>{assignment.className || 'All'}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant={bookmarkedIds[`assignment:${assignment._id}`] ? 'contained' : 'outlined'}
                              color="warning"
                              startIcon={bookmarkedIds[`assignment:${assignment._id}`] ? <Bookmark /> : <BookmarkBorder />}
                              onClick={() => handleToggleBookmark(assignment)}
                            >
                              {bookmarkedIds[`assignment:${assignment._id}`] ? 'Saved' : 'Save'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/school-work/assignments/${assignment._id}`)}
                            >
                              View
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentAssignments;
